import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { lessonsAPI, jobApiError } from '../services/api';
import { FiRefreshCw, FiAlertCircle, FiPlus, FiX, FiSearch, FiBookOpen } from 'react-icons/fi';
import { LearnerFields, EMPTY_LEARNER, SamePerson } from './LanguageLessons';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

// Learners: the client type for people who only take lessons and are not
// students, job seekers, tourists or medical clients. Lessons, packages and
// tests stay on the Language Lessons page; this is their client record.
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const LessonsLeft = ({ n }) => (
  <span className="status-pill" style={{ color: n <= 0 ? '#b91c1c' : n <= 2 ? '#b45309' : '#15803d' }}>
    {n <= 0 ? 'No lessons left' : `${n} lesson(s) left`}
  </span>
);

const LearnerModal = ({ initial, onSaved, onCancel }) => {
  const isEdit = !!initial?.id;
  const navigate = useNavigate();
  const [f, setF] = useState(isEdit ? { ...EMPTY_LEARNER, ...initial, phone: initial.phone || '', date_of_birth: initial.date_of_birth || '' } : EMPTY_LEARNER);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const l = initial?.lessons;

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, date_of_birth: f.date_of_birth || null };
      if (isEdit) await lessonsAPI.updateWalkin(initial.id, body);
      else await lessonsAPI.createWalkin(body);
      onSaved();
    } catch (e) {
      setErr(jobApiError(e, 'Could not save the learner.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div>
            <h2>{isEdit ? `${initial.first_name} ${initial.last_name}` : 'New Learner'}</h2>
            {isEdit && <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>{initial.learner_id}</div>}
          </div>
          <button className="modal-close" onClick={onCancel}><FiX /></button>
        </div>
        <div className="modal-form">
          {err && <div className="error-message">{err}</div>}
          <LearnerFields f={f} set={set} />
          {!isEdit && (
            <SamePerson f={f} useLabel="This is them: open their lessons"
              onUse={async (c) => {
                try {
                  const r = c.learner_id ? { data: { id: c.learner_id } } : await lessonsAPI.startLearner({ kind: c.kind, client_id: c.client_id });
                  navigate(`/language-lessons?learner=${r.data.id}`);
                } catch (e) { setErr(jobApiError(e)); }
              }} />
          )}
          <div className="form-group"><label>Notes</label><textarea rows={3} value={f.notes} onChange={(e) => set('notes', e.target.value)} /></div>

          {isEdit && (
            <div className="cv-subsection">
              <div className="cv-subsection-title"><span>Turkish lessons</span></div>
              {l ? (
                <div style={{ fontSize: 13 }}>
                  <div style={{ marginBottom: 4 }}>Teacher: <strong>{l.teachers.length ? l.teachers.join(', ') : 'none yet'}</strong></div>
                  <LessonsLeft n={l.lessons_left} />{' '}
                  Level: {l.level || 'not set'}{l.progress.total ? ` (${l.progress.done} of ${l.progress.total} sub-topics)` : ''}
                  {' · '}Last lesson: {l.last_lesson_at ? fmtDate(l.last_lesson_at) : 'none yet'}
                  {l.status !== 'ACTIVE' && ` · ${l.status.toLowerCase()}`}
                </div>
              ) : <div className="cv-subsection-empty">Not taking lessons.</div>}
              {initial.lesson_learner_id && (
                <button type="button" className="btn-secondary" style={{ marginTop: 8 }}
                  onClick={() => navigate(`/language-lessons?learner=${initial.lesson_learner_id}`)}>
                  <FiBookOpen /> Classes, packages and tests
                </button>
              )}
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" disabled={saving || !f.first_name.trim() || !f.last_name.trim() || !f.email.trim()} onClick={save}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Learner'}
          </button>
        </div>
      </div>
    </div>
  );
};

const LearnerClients = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [dups, setDups] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows((await lessonsAPI.walkins(search ? { search } : {})).data);
    } catch (e) {
      setError(e?.response?.status === 403 ? 'Learners are for leadership, admins and consultants.' : jobApiError(e, 'Failed to load learners.'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { lessonsAPI.duplicates().then((r) => setDups(r.data)).catch(() => setDups([])); }, [rows]);

  return (
    <div className="students-page">
      <div className="page-header">
        <div className="header-actions-left">
          <button className="btn-primary" onClick={() => setModal({})}><FiPlus /> New Learner</button>
        </div>
        <div className="header-actions-right">
          <div className="search-box">
            <FiSearch />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, email, phone, learner ID…" />
          </div>
          <button className="btn-icon" onClick={load} title="Refresh"><FiRefreshCw className={loading ? 'cv-spin' : ''} /></button>
        </div>
      </div>
      <div style={{ color: '#64748b', fontSize: 13, margin: '0 0 12px' }}>
        People who only take lessons. Students, job seekers, tourists and medical clients who take lessons stay in their own lists.
      </div>

      {dups.length > 0 && (
        <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 13 }}>
          <strong style={{ color: '#92400e' }}>{dups.length} learner{dups.length === 1 ? '' : 's'} may already be in the system as another client.</strong>{' '}
          Open each one and link it if it is the same person, so one person has one record:{' '}
          {dups.map((d) => (
            <button key={d.learner_id} className="btn-secondary" style={{ margin: 2 }} onClick={() => navigate(`/language-lessons?learner=${d.learner_id}`)}>
              {d.name} → {d.matches[0].kind_label} {d.matches[0].ref}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiAlertCircle size={16} /><span style={{ flex: 1 }}>{error}</span>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty-state">No learners found.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr><th>Learner ID</th><th>Name</th><th>Phone</th><th>Teacher</th><th>Level</th><th>Lessons</th><th>Added</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setModal(r)} style={{ cursor: 'pointer' }}>
                  <td><span className="cv-id">{r.learner_id}</span></td>
                  <td><strong>{r.first_name} {r.last_name}</strong><div style={{ color: '#999', fontSize: 12 }}>{r.email}</div></td>
                  <td>{r.phone || '—'}</td>
                  <td>{r.lessons?.teachers?.length ? r.lessons.teachers.join(', ') : '—'}</td>
                  <td>{r.lessons?.level || '—'}</td>
                  <td>{r.lessons ? <LessonsLeft n={r.lessons.lessons_left} /> : '—'}</td>
                  <td>{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && <LearnerModal initial={modal.id ? modal : null} onCancel={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
};

export default LearnerClients;
